import { useEffect, useMemo, useState } from "react"
import { Virtuoso } from "react-virtuoso"
import "../styles/ChatMobile.css"
import { API_ROUTES } from "../../../core/Constants"
import { hasRole, setMobileToolbar, useGlobal, useHub, useScreen } from "../../../core/Global"
import ChatMobileRow from "./ChatMobileRow"
import Icon from "../../../components/base/Icon"
import { isNativeiOSApp } from "../../../NativeAppBrdige"

interface IProps {
    readedChatId?: string
    archivedOpen?: boolean
    onNewChat: () => void
    onOpenArchived: () => void
}

export default function ChatMobileList(props: IProps) {

    const hub = useHub()
    const screen = useScreen()
    const [globalState] = useGlobal()

    const [archivedCount, archivedCountSet] = useState<number>(0)
    const [boxHeight, boxHeightSet] = useState<number>()

    const items = useMemo(() => {
        const base = globalState.chatlist ?? []
        // The Office and Rinvox chats sort like any other: pinned first, then by last message.
        return [...base].sort((a, b) => {
            if (a.pinned && !b.pinned) return -1
            if (!a.pinned && b.pinned) return 1
            const dateA = new Date(a.lastUpdateDate!).getTime()
            const dateB = new Date(b.lastUpdateDate!).getTime()
            return dateB - dateA
        })
    }, [globalState.chatlist])

    const showEntry = archivedCount > 0

    const fixSizes = () => {
        let h = screen.height
        h = h - ($("#kt_header").outerHeight() ?? 50)
        boxHeightSet(h)
    }

    useEffect(() => {
        fixSizes()
        window.addEventListener("resize", fixSizes)
        return () => window.removeEventListener("resize", fixSizes)
    }, [screen.height])

    // Also re-read on close, since chats can be unarchived from inside the drawer.
    useEffect(() => {
        if (props.archivedOpen) return
        hub.Get(API_ROUTES.Chat + "hasarchived", res => archivedCountSet(res.data ?? 0))
    }, [props.archivedOpen])

    // ---- header action bar: new chat only ----
    useEffect(() => {
        const Toolbar = () => (
            <div className="d-flex align-items-center chat-mobile-actions">
                {hasRole("ChatStart") &&
                    <div className="chat-mobile-action" onClick={props.onNewChat}>
                        <Icon icon="pen-to-square" size={19} type="regular" />
                    </div>}
            </div>
        )
        setMobileToolbar(Toolbar)
        return () => { setMobileToolbar(undefined) }
    }, [])

    const footerSpacer = () => (
        <div style={{ height: isNativeiOSApp() ? 120 : 100 }} />
    )

    // scrolling "Archived" entry row (Telegram-style) — opens the archived drawer
    const ArchivedHeader = () => (
        <div className="chat-mobile-archived" onClick={props.onOpenArchived}>
            <div className="chat-mobile-archived-icon">
                <Icon icon="box-archive" type="solid" size={16} />
            </div>
            <span className="chat-mobile-archived-label">Archived</span>
            <span className="chat-mobile-archived-count">{archivedCount}</span>
            <Icon icon="chevron-right" size={13} className="text-muted" />
        </div>
    )

    return (
        <div className="chat-mobile-list">
            {
                (items.length == 0 && !showEntry) ?
                    <div className="chat-mobile-empty" style={{ height: boxHeight }}>
                        {
                            hasRole("ChatStart") ?
                                <button className="btn btn-sm btn-primary" onClick={props.onNewChat}>
                                    <Icon icon="edit" /> Start New Chat
                                </button>
                                : "No chats yet."
                        }
                    </div>
                    :
                    <Virtuoso
                        style={{ height: boxHeight }}
                        totalCount={items.length}
                        components={showEntry
                            ? { Header: ArchivedHeader, Footer: footerSpacer }
                            : { Footer: footerSpacer }}
                        itemContent={index => {
                            const item = items[index]
                            return (
                                <ChatMobileRow key={item.id} item={item}
                                    seperator={index + 1 < items.length}
                                    readed={item.chatId == props.readedChatId} />
                            )
                        }} />
            }
        </div>
    )
}
